import z from "zod";
import type { AgentClient } from "@/agent/client/class";
import type { AgentClientDefinition } from "@/agent/client/types";
import { canon, type SerializableValue } from "@/shared/canon";
import { deepClone } from "@/shared/deep-clone";
import { lifeError } from "@/shared/error";
import { toMethodName } from "@/shared/method-name";
import * as op from "@/shared/operation";
import { newId } from "@/shared/prefixed-id";
import type { Any } from "@/shared/types";
import type { TelemetryClient } from "@/telemetry/clients/base";
import { createTelemetryClient } from "@/telemetry/clients/browser";
import { type PluginAccessor, type PluginContext, pluginEventInputSchema } from "../server/types";
import type {
  PluginClientAccessor,
  PluginClientAtoms,
  PluginClientConfig,
  PluginClientContextListener,
  PluginClientDefinition,
  PluginClientDependenciesAccessor,
  PluginClientEventsListener,
  PluginClientExtension,
} from "./types";

export class PluginClient<const PluginClientDef extends PluginClientDefinition> {
  readonly def: PluginClientDef;
  readonly #config: PluginClientConfig<PluginClientDef["config"], "output">;
  readonly #agent: AgentClient<AgentClientDefinition>;
  readonly #telemetry: TelemetryClient;
  readonly #atoms: PluginClientAtoms<PluginClientDef["atoms"]>;
  readonly #extension: PluginClientExtension<PluginClientDef["class"]>;
  readonly #eventsListeners = new Map<string, PluginClientEventsListener>();
  readonly #contextListeners = new Map<string, PluginClientContextListener>();
  #contextValue = {} as PluginContext<PluginClientDef["$serverDef"]["context"], "output">;
  #lastContextValueTimestamp = 0;

  constructor(
    definition: PluginClientDef,
    config: PluginClientConfig<PluginClientDef["config"], "input">,
    agent: AgentClient<AgentClientDefinition>,
  ) {
    this.def = definition;
    this.#agent = agent;

    // Validate config
    const { error: errConfig, data: parsedConfig } = this.def.config.schema.safeParse(config);
    if (errConfig) {
      throw lifeError({
        code: "Validation",
        message: `Invalid config provided to plugin client '${this.def.name}'.`,
        cause: errConfig,
      });
    }
    this.#config = parsedConfig as PluginClientConfig<PluginClientDef["config"], "output">;

    // Initialize telemetry
    this.#telemetry = createTelemetryClient("plugin.client", {
      agentId: agent.id,
      agentName: agent.def.name,
      agentConfig: agent.config,
      transportProviderName: agent.config.transport.provider,
      pluginName: definition.name,
      pluginClientConfig: this.#config,
    });

    // Instantiate extension
    const [errAccessor, accessor] = this.getAccessor();
    if (errAccessor) throw errAccessor;
    this.#extension = new (definition.class({
      plugin: op.toPublic(accessor),
      agent: op.toPublic(this.#agent),
      dependencies: this.getDependenciesAccessor(),
      telemetry: this.#telemetry,
    }))();

    // Compute atoms
    const atomsArr = definition.atoms({
      plugin: op.toPublic(accessor),
      agent: op.toPublic(this.#agent),
      dependencies: this.getDependenciesAccessor(),
      telemetry: this.#telemetry,
    });
    const atomsMap = Object.fromEntries(atomsArr.map((atom) => [atom.name, atom.create]));
    this.#atoms = atomsMap as PluginClientAtoms<PluginClientDef["atoms"]>;
  }

  getAccessor() {
    // client.config
    const [errClone, cloneConfig] = op.attempt(() => deepClone(this.#config));
    if (errClone) return op.failure({ code: "Unknown", cause: errClone });

    // server.context.get()
    const serverContextGet = (() =>
      op.attempt(() => deepClone(this.#contextValue))) satisfies PluginAccessor<
      PluginClientDef["$serverDef"],
      { type: "client"; name: string }
    >["context"]["get"];

    // server.context.onChange()
    const serverContextOnChange = ((selector, callback) => {
      const id = newId("listener");
      this.#contextListeners.set(id, { id, callback, selector });
      return op.success(() => this.#contextListeners.delete(id));
    }) satisfies PluginAccessor<
      PluginClientDef["$serverDef"],
      { type: "client"; name: string }
    >["context"]["onChange"];

    // server.events.emit()
    const serverEventsEmit = (async (event) => {
      const [err, data] = await this.#agent.transport.call({
        name: `plugin.${this.def.name}.events.emit`,
        schema: {
          input: pluginEventInputSchema,
          output: z.object({ id: z.string() }),
        },
        input: event as unknown as z.infer<typeof pluginEventInputSchema>,
      });
      if (err) return op.failure(err);
      return op.success(data.id);
    }) satisfies PluginAccessor<
      PluginClientDef["$serverDef"],
      { type: "client"; name: string }
    >["events"]["emit"];

    // server.events.on()
    const serverEventsOn = ((selector, callback, includeDropped = false) => {
      // Append listener, and ask plugin's server to stream selected event
      const id = newId("listener");
      this.#eventsListeners.set(id, { id, selector, callback, includeDropped });
      this.#agent.transport.call({
        name: `plugin.${this.def.name}.events.subscribe`,
        schema: {
          input: z.object({
            listenerId: z.string(),
            selector: z.any(),
            includeDropped: z.boolean().prefault(false),
          }),
        },
        input: { listenerId: id, selector, includeDropped },
      });

      // Return unsubscribe function
      return op.success(() => {
        this.#agent.transport.call({
          name: `plugin.${this.def.name}.events.unsubscribe`,
          schema: { input: z.object({ listenerId: z.string() }) },
          input: { listenerId: id },
        });
        this.#eventsListeners.delete(id);
      });
    }) satisfies PluginAccessor<
      PluginClientDef["$serverDef"],
      { type: "client"; name: string }
    >["events"]["on"];

    // server.events.once()
    const serverEventsOnce = ((selector, callback, includeDropped = false) => {
      const [errOn, unsubscribe] = serverEventsOn(
        selector,
        async (event) => {
          unsubscribe?.();
          await callback(event);
        },
        includeDropped,
      );
      if (errOn) return op.failure(errOn);
      return op.success(unsubscribe);
    }) satisfies PluginAccessor<
      PluginClientDef["$serverDef"],
      { type: "client"; name: string }
    >["events"]["once"];

    // server.events.waitForProcessing()
    const serverEventsWaitForProcessing = (async (eventId) =>
      await this.#agent.transport.call({
        name: `plugin.${this.def.name}.events.waitForProcessing`,
        schema: { input: z.object({ eventId: z.string() }) },
        input: { eventId },
      })) satisfies PluginAccessor<
      PluginClientDef["$serverDef"],
      { type: "client"; name: string }
    >["events"]["waitForProcessing"];

    // server.events.waitForResult()
    const serverEventsWaitForResult = (async (eventId, handlerName) =>
      // @ts-expect-error
      await this.#agent.transport.call({
        name: `plugin.${this.def.name}.events.waitForResult`,
        schema: {
          input: z.object({ eventId: z.string(), handlerName: z.string() }),
          output: z.object().loose(),
        },
        input: { eventId, handlerName },
      })) satisfies PluginAccessor<
      PluginClientDef["$serverDef"],
      { type: "client"; name: string }
    >["events"]["waitForResult"];

    return op.success(
      Object.assign(this.#extension ?? ({} as InstanceType<ReturnType<PluginClientDef["class"]>>), {
        config: cloneConfig,
        atoms: this.#atoms,
        server: {
          context: {
            onChange: serverContextOnChange,
            get: serverContextGet,
          },
          events: {
            emit: serverEventsEmit,
            on: serverEventsOn,
            once: serverEventsOnce,
            waitForProcessing: serverEventsWaitForProcessing,
            waitForResult: serverEventsWaitForResult as Any,
          },
        },
      }) satisfies PluginClientAccessor<PluginClientDef, Any>,
    );
  }

  getDependenciesAccessor() {
    const dependenciesAccessors = {} as PluginClientDependenciesAccessor<
      PluginClientDef["dependencies"]
    >;
    for (const dependencyDef of this.def.dependencies ?? []) {
      // @ts-expect-error
      const accessor = this.#agent[toMethodName(dependencyDef.name)];
      if (!accessor)
        return op.failure({
          code: "NotFound",
          message: `Failed to obtain client instance for dependency plugin '${dependencyDef.name}'. Shouldn't happen.`,
        });
      // @ts-expect-error
      dependenciesAccessors[dependencyDef.name] = accessor;
    }
    return op.success(dependenciesAccessors);
  }

  async start() {
    // Figure whether the plugin client has a server
    const [errPing, dataPing] = await this.#agent.transport.call({
      name: "agent.has-plugin-server",
      schema: {
        input: z.object({ pluginName: z.string() }),
        output: z.object({
          hasServer: z.boolean(),
        }),
      },
      input: { pluginName: this.def.name },
    });
    if (errPing) return op.failure(errPing);

    // Return early if the plugin client has no server
    if (!dataPing.hasServer) return op.success();

    // Listen to context value changes
    this.#agent.transport.register({
      name: `plugin.${this.def.name}.context.changed`,
      schema: { input: z.object({ value: z.any(), timestamp: z.number() }) },
      execute: async ({ value, timestamp }) => await this.#setContextValue(value, timestamp),
    });

    // Consume events listeners callbacks
    this.#agent.transport.register({
      name: `plugin.${this.def.name}.events.callback`,
      schema: {
        input: z.object({ listenerId: z.string(), event: pluginEventInputSchema }),
      },
      execute: async ({ listenerId, event }) => {
        await this.#eventsListeners.get(listenerId)?.callback(event as never);
        return op.success();
      },
    });

    // Fetch initial context value
    const [err, data] = await this.#agent.transport.call({
      name: `plugin.${this.def.name}.context.get`,
      schema: { output: z.object({ value: z.any(), timestamp: z.number() }) },
    });
    if (err) return op.failure(err);
    return await this.#setContextValue(data.value, data.timestamp);
  }

  async #setContextValue(
    value: PluginContext<PluginClientDef["$serverDef"]["context"], "output">,
    timestamp: number,
  ) {
    // Clone the old context value
    const oldContextValue = deepClone(this.#contextValue);

    // Ensure the new value is newer than the last one
    if (timestamp < this.#lastContextValueTimestamp) return op.success();

    // Update the last context value timestamp and value
    this.#lastContextValueTimestamp = timestamp;
    this.#contextValue = value;

    // Notify listeners if the value they select has changed
    await Promise.all(
      Array.from(this.#contextListeners.values()).map(async (listener) => {
        const newSelectedValue = listener.selector(this.#contextValue) as SerializableValue;
        const oldSelectedValue = listener.selector(oldContextValue) as SerializableValue;

        // Check if the value actually changed
        const [errEqual, equal] = canon.equal(newSelectedValue, oldSelectedValue);
        if (errEqual) return op.failure(errEqual);
        if (equal) return op.success();

        // Call the listener if changed
        return await op.attempt(
          async () =>
            await listener.callback(deepClone(this.#contextValue), deepClone(oldContextValue)),
        );
      }),
    );
    return op.success();
  }
}
